import axios from 'axios';

/**
 * A simple constructor to turn ordinary objects into
 * `ReportsMetadata` instances, which is useful in prop validation.
 * @param {Object} data
 */
export class ReportsMetadata {
	constructor(data) {
		Object.assign(this, data);
	}
}

/**
 * A collection of documents all pertaining to the same policy.
 */
export class ReportPacket {
	public terms: ReportTerms[];
	public policy_no: string;
	public policy_holder_name: string;

	constructor(data) {
		Object.assign(this, data);
	}
}

export class ReportTerms {
	public effective_from: any;
	public effective_to: any;
	public risks: Risk[];
}

export class Risk {
	public risk_name: any;
	public report_id: any;
	public effective_from_date: any;
	public effective_to_date: any;
	public selected: boolean;
	public pceOption: boolean;
	public claimant: string;
}

export enum ClaimantNameOption {
	exclude = 'false',
	include = 'true',
}

export enum PceOption {
	exclude = 'Exclude',
	include = 'Include',
	only = 'Only',
}

export enum ReportFormat {
	excel = 'EXCEL',
	pdf = 'PDF',
}

const Reports = {
	requestAccess: async data => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI2 + '/reports-access',
			data,
		});
	},
	/**
	 * Gets all the reports for the given @param policyNumber.
	 *
	 * @param {string} policyNumber
	 * @returns {Promise<Array<ReportsMetadata>>}
	 */
	getCredentialReports: async policyNumber => {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/reports/?policy_no=${policyNumber}` +
				'&report_type=credential',
		});

		return response.data ? new ReportPacket(response.data) : {};
	},

	/**
	 * Gets all the reports for the given @param policyNumber.
	 *
	 * @param {string} policyNumber
	 * @returns {Promise<Array<ReportsMetadata>>}
	 */
	getLossHistoryReports: async policyNumber => {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/reports/?policy_no=${policyNumber}` +
				'&report_type=loss',
		});

		return response.data ? new ReportPacket(response.data) : {};
	},

	async requestCredentialReport(
		reportId,
		reportType,
	): Promise<{ fetching: boolean; s3_url?: string }> {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/reports/${reportId}/` +
				'?report_type=' +
				reportType +
				'&report_format=' +
				'PDF',
		});

		return response.data;
	},

	async requestLossHistoryReport(
		reportId: string,
		claimantNameOption: ClaimantNameOption,
		pceOption: PceOption,
		insuredType: string,
		reportFormat: ReportFormat,
		terms: string[],
		downloadId: string,
	): Promise<{ fetching: boolean; s3_url?: string }> {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/reports/${reportId}/` +
				`?report_type=loss` +
				`&claimant=${claimantNameOption}` +
				`&pce_setting=${pceOption}` +
				`&insured_type=${insuredType}` +
				`&report_format=${reportFormat}` +
				`&terms=${terms}` +
				`&downloadId=${downloadId}`,
		});
		return response.data;
	},

	/**
	 * Request Policy document with the given `policyNumber` and `id` and `year`.
	 *
	 * @param {string} id
	 * @returns {Promise<string>}
	 */
	async requestLossReport(reportId, reportType, claimant, pceSetting, insuredType) {
		const response = await axios({
			method: 'GET',
			url:
				`${process.env.VUE_APP_API_URI2}/reports/${reportId}/` +
				'?reportType=' +
				reportType +
				'&claimant=' +
				claimant +
				'&pce_setting=' +
				pceSetting +
				'&insured_type=' +
				insuredType,
		});

		return response.data;
	},

	/**
	 * Request Policy document with the given `policyNumber` and `id` and `year`.
	 *
	 * @param {string} id
	 * @returns {Promise<string>}
	 */
	async fetchReport(s3Url) {
		// @ts-ignore
		const response = await axios({
			method: 'GET',
			url: s3Url,
			headers: {
				'Content-Type': 'application/pdf',
			},
			skipAuth: true,
		});

		return response.data;
	},

	hardCodeData(report) {
		report[0].terms = [report[0].terms[0].risks[0], report[0].terms[1].risks[0]];
		return report;
	},

	generateFileName(params: {
		date?: string;
		extensionWithoutSeparator: string;
		policyNumber: string;
		subtitle?: string;
		title: string;
	}): string {
		return (
			[params.title, params.subtitle, params.policyNumber, params.date]
				.filter((param: string | undefined): boolean => !!param)
				.join(' ') + `.${params.extensionWithoutSeparator}`
		);
	},
};

export default Reports;
